import type { FrameLook, FrameSpec } from './frames';
import { ACETATE_LIBRARY, isSeeThrough, type AcetateSpec } from './acetates';

/** A library acetate as offered on every opted-in Handmade model (no stock/variant mapping implied). */
export interface AcetatePreset extends AcetateSpec {
  /** Fallback label (UI copy comes from `words().acetates`). */
  label: string;
  /** Alpha of the transparent-export fallback (no camera behind): 1 for solid plates. */
  opacity: number;
}
/** Fallback alpha when no camera image is behind the frame: the apparent colour reads solid. */
export function legacyOpacity(spec: Pick<AcetateSpec, 'transmission'>): number {
  return isSeeThrough(spec) ? Math.round((1 - 0.3 * spec.transmission) * 100) / 100 : 1;
}
/** The 36 supplier plates, in the order of the chips. */
export const ACETATES: AcetatePreset[] = ACETATE_LIBRARY.map(a => ({
  ...a, label: `${a.qualities.join(', ')} · ${a.ref}`, opacity: legacyOpacity(a),
}));
export const LENS_TINTS = [
  { id: 'brown', color: '#785238', transmission: [0.72, 0.49, 0.34] },
  { id: 'green', color: '#315747', transmission: [0.44, 0.64, 0.50] },
  { id: 'grey', color: '#666666', transmission: [0.58, 0.58, 0.58] },
  { id: 'blue', color: '#436b98', transmission: [0.40, 0.56, 0.75] },
] as const;
/**
 * A200 — Signature lenses (David, 8 October): the four classic tints and « Lunettes de vue »
 * (the optical version: clear lenses), no gradient, no Sunset, no other change. The frame keeps
 * its own acetate: the other colourways are separate products, in the model list.
 */
export const CLEAR_LENS = { id: 'clear', color: '#eef2f1', transmission: [0.96, 0.965, 0.96] } as const;
export const SIGNATURE_LENS_IDS = ['brown', 'grey', 'green', 'blue', 'clear'] as const;
/** Density of a Signature tint (the « dark » classic tint, as on the Handmade lens discs). */
export const SIGNATURE_TINT_DENSITY = 85;
/** A202 (David, 11 octobre : « les verres Sunset pour les Signature, c'est une addition de 990k ») : the supplement. */
export const SIGNATURE_SUNSET_SUPPLEMENT_IDR = 990000;
/**
 * A202 (David, 11 octobre : « oui » à l'ajout automatique) : the store product that carries the supplement in the cart,
 * added with a Signature in Sunset lenses — « Plano Lens », variant « Special Tint » (Rp 990.000).
 */
export const SIGNATURE_SUNSET_SUPPLEMENT_PRODUCT = { handle: 'plano-lens', variant: 'Special Tint' } as const;
/** « Rp 990.000 » — the store's format (IDR, dot thousands, no decimals). */
export function formatIdr(amount: number): string {
  return `Rp ${Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
}
/** A202: the Sunset lens ids (the eleven lenses of the Sunset tab). */
export function isSunsetId(id: string): boolean {
  return SPECIAL_LENSES.some(s => s.id === id);
}
/** Lens choices that a frame accepts: everything on opted-in Handmade, the Signature set on Signature. */
export function lensOnly(spec: Pick<FrameSpec, 'line' | 'customizable'>): boolean {
  return spec.line === 'Classic';
}
/** Maison Mata Sunset Lenses swatch colours (visual previews, not measured HOYA transmission). */
export const SPECIAL_LENSES = [
  { id: 'sunset', label: 'Sunset', top: '#3d38a8', bottom: '#ff7a2e' },
  { id: 'sunrise', label: 'Sunrise', top: '#78aee6', bottom: '#ffd83a' },
  { id: 'dawn', label: 'Dawn', top: '#5a6eaa', bottom: '#ff8a6e' },
  { id: 'stellar', label: 'Stellar', top: '#1e78ff', bottom: '#eb285a' },
  { id: 'aurora', label: 'Aurora', top: '#965adc', bottom: '#96c8ff' },
  { id: 'bloom', label: 'Bloom', top: '#9a8e90', bottom: '#f0508a' },
  { id: 'magma', label: 'Magma', top: '#f03228', bottom: '#f03228' },
  { id: 'coral', label: 'Coral', top: '#8c1e3c', bottom: '#d8a0dc' },
  { id: 'sun', label: 'Sun', top: '#ffd228', bottom: '#ffd228' },
  { id: 'sky', label: 'Sky', top: '#2e70e8', bottom: '#2e70e8' },
  { id: 'lagoon', label: 'Lagoon', top: '#8fd2e0', bottom: '#264b5a' },
] as const;
function previewTransmission(hex: string, density: number): [number, number, number] {
  const rgb = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
  const max = Math.max(...rgb);
  return rgb.map(v => Math.pow(0.25 + 0.75 * v / max, density / 35)) as [number, number, number];
}
export interface AppearanceChoice { acetate: string; lens: string; density: number; gradient: boolean }
export const DEFAULT_APPEARANCE: AppearanceChoice = { acetate: 'original', lens: 'original', density: 50, gradient: false };
export function defaultAppearance(spec: FrameSpec): AppearanceChoice {
  return { ...DEFAULT_APPEARANCE, gradient: (spec.look.lensGradient ?? 0) > 0 };
}
export function appearanceLook(spec: FrameSpec, choice: AppearanceChoice): FrameLook {
  if (lensOnly(spec)) return signatureLook(spec, choice);
  if (spec.line !== 'Handmade' || !spec.customizable) throw new Error('This frame does not allow material previews');
  const acetate = ACETATES.find(a => a.id === choice.acetate);
  if (choice.acetate !== 'original' && !acetate) throw new Error('Unknown acetate');
  const lens = LENS_TINTS.find(a => a.id === choice.lens);
  const special = SPECIAL_LENSES.find(a => a.id === choice.lens);
  // A201 (David, 11 octobre : « les verres lunettes de vue des Signature, c'est parfait, je voudrais le faire aussi
  // sur les Handmade ») : the same clear lenses, with any acetate.
  const clear = choice.lens === CLEAR_LENS.id;
  if (choice.lens !== 'original' && !lens && !special && !clear) throw new Error('Unknown lens');
  if (!Number.isFinite(choice.density) || choice.density < 0 || choice.density > 100) throw new Error('Invalid lens density');
  const look = { ...spec.look };
  // Read by the specular tint, the procedural edges and the title: the plate's apparent colour.
  // A110Y2 (Astra A143): a chosen acetate also replaces a two-colour construction's own temple acetate.
  if (acetate) Object.assign(look, { acetateColor: acetate.color, acetateOpacity: acetate.opacity, acetatePhotoOpacity: acetate.opacity, acetateRoughness: acetate.roughness, templeAcetateColor: undefined, templeAcetateOpacity: undefined, templeAcetatePhotoOpacity: undefined });
  if (clear) return clearLensLook(look);
  // At the initial setting, preserve the exact product reference transmission.
  if (special) return sunsetLook(look, special, choice.density);
  if (!lens && choice.lens === 'original' && spec.look.lensTransmissionTop && spec.look.lensTransmissionBottom) {
    const exponent = choice.density / 50;
    const scale = (values: [number, number, number]) =>
      values.map(t => Math.pow(t, exponent)) as [number, number, number];
    look.lensTransmission = scale(spec.look.lensTransmission ?? [0.5, 0.5, 0.5]);
    look.lensTransmissionTop = choice.gradient ? scale(spec.look.lensTransmissionTop) : undefined;
    look.lensTransmissionBottom = choice.gradient ? scale(spec.look.lensTransmissionBottom) : undefined;
    look.lensOpacity = Math.min(1, spec.look.lensOpacity * exponent);
    look.lensGradient = choice.gradient ? (spec.look.lensGradient || 0.8) : 0;
    return look;
  }
  delete look.lensTransmissionTop;
  delete look.lensTransmissionBottom;
  const reference = lens?.transmission ?? spec.look.lensTransmission ?? [0.5, 0.5, 0.5];
  const exponent = lens ? choice.density / 35 : choice.density / 50;
  look.lensTransmission = reference.map(t => Math.pow(t, exponent)) as [number, number, number];
  look.lensColor = lens?.color ?? spec.look.lensColor;
  look.lensOpacity = lens ? choice.density / 100 : Math.min(1, spec.look.lensOpacity * exponent);
  look.lensGradient = choice.gradient ? (lens ? 0.8 : (spec.look.lensGradient || 0.8)) : 0;
  return look;
}

/**
 * A200: a Signature frame with a lens choice. The acetate is always the product's own; « Original »
 * is the exact product reference, a tint is the classic tint, « clear » the optical version.
 */
export function signatureLook(spec: FrameSpec, choice: AppearanceChoice): FrameLook {
  if (choice.acetate !== 'original') throw new Error('Signature frames keep their own acetate');
  // A202: the eleven Sunset lenses too (a supplement, `SIGNATURE_SUNSET_SUPPLEMENT_IDR`).
  const special = SPECIAL_LENSES.find(a => a.id === choice.lens);
  if (choice.lens !== 'original' && !special && !(SIGNATURE_LENS_IDS as readonly string[]).includes(choice.lens)) throw new Error('Unknown lens');
  if (special && (!Number.isFinite(choice.density) || choice.density < 0 || choice.density > 100)) throw new Error('Invalid lens density');
  const look = { ...spec.look };
  if (choice.lens === 'original') return look;
  if (special) return sunsetLook(look, special, choice.density);
  delete look.lensTransmissionTop;
  delete look.lensTransmissionBottom;
  look.lensGradient = 0;
  if (choice.lens === CLEAR_LENS.id) return clearLensLook(look);
  const tint = LENS_TINTS.find(l => l.id === choice.lens)!;
  const exponent = SIGNATURE_TINT_DENSITY / 35;
  look.lensTransmission = tint.transmission.map(t => Math.pow(t, exponent)) as [number, number, number];
  look.lensColor = tint.color;
  look.lensOpacity = SIGNATURE_TINT_DENSITY / 100;
  return look;
}

/** « Lunettes de vue » : clear lenses (a light sheen), no tint, no gradient — Signature and Handmade alike. */
function clearLensLook(look: FrameLook): FrameLook {
  delete look.lensTransmissionTop;
  delete look.lensTransmissionBottom;
  look.lensGradient = 0;
  look.lensColor = CLEAR_LENS.color;
  look.lensTransmission = [...CLEAR_LENS.transmission];
  look.lensOpacity = 0.06;
  return look;
}

/** A Sunset lens: its fixed two-tone treatment at a density (Handmade and, since A202, Signature). */
function sunsetLook(look: FrameLook, special: typeof SPECIAL_LENSES[number], density: number): FrameLook {
  const top = previewTransmission(special.top, density);
  const bottom = previewTransmission(special.bottom, density);
  // Sunset colours have a fixed two-tone treatment, independent of the
  // optional clear-bottom gradient offered for standard lenses.
  look.lensTransmissionTop = top;
  look.lensTransmissionBottom = bottom;
  look.lensTransmission = top.map((v, i) => (v + bottom[i]!) / 2) as [number, number, number];
  look.lensColor = special.top;
  look.lensOpacity = density / 100;
  look.lensGradient = 0;
  return look;
}
